import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { AlertTriangle, Ban } from "lucide-react";
import { useFeatureMode, isFinancialExecutionAllowed } from "../hooks/useFeatureMode";

// OCI V2.19 — Non-blocking visibility so a campaign that is not set up for live
// donations doesn't silently accept inert signups. Renders nothing when the
// campaign is payment+delivery ready (or while loading / on error).
// OCI V2.27 — audience split: fundraisers get a plain "contact your manager"
// instruction with NO payment/Stripe/routing jargon; managers/owners get the
// real technical reasons. Default audience is fundraiser (safest).
export default function PaymentReadinessBanner({ campaignId, className = "", audience = "fundraiser" }) {
  const [state, setState] = useState(null);
  const featureMode = useFeatureMode();

  useEffect(() => {
    if (!campaignId) { setState(null); return; }
    let alive = true;
    (async () => {
      try {
        const r = await apiClient.get(`/campaigns/${campaignId}/payment-readiness`);
        if (alive) setState(r.data);
      } catch {
        if (alive) setState(null);
      }
    })();
    return () => { alive = false; };
  }, [campaignId]);

  // Package 0 Safety Freeze — payment readiness is irrelevant in statements-only mode.
  if (!isFinancialExecutionAllowed(featureMode)) return null;

  if (!state || state.ready) return null;

  const blocked = state.severity === "blocked";
  const tone = blocked
    ? "bg-rose-50 border-rose-200 text-rose-800"
    : "bg-amber-50 border-amber-200 text-amber-800";
  const Icon = blocked ? Ban : AlertTriangle;
  const isManager = audience === "manager";

  return (
    <div className={`rounded-lg border px-4 py-3 flex items-start gap-3 ${tone} ${className}`}
         data-testid="payment-readiness-banner"
         data-audience={audience}
         data-severity={state.severity}>
      <Icon className="h-5 w-5 shrink-0 mt-0.5" />
      <div className="text-sm">
        {isManager ? (
          <>
            <div className="font-semibold">
              {blocked
                ? "This campaign can't take live donations yet"
                : "This campaign isn't fully set up for live donations"}
            </div>
            <p className="mt-0.5 opacity-90">
              Sign-ups are recorded, but {blocked ? "payments are blocked" : "won't be auto-collected or delivered to the charity"} until set-up is complete.
            </p>
            {Array.isArray(state.reasons) && state.reasons.length > 0 && (
              <ul className="mt-1.5 list-disc list-inside space-y-0.5 opacity-80" data-testid="payment-readiness-reasons">
                {state.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            )}
          </>
        ) : (
          <>
            <div className="font-semibold">This campaign isn't ready to take donations yet</div>
            <p className="mt-0.5 opacity-90">
              Please contact your manager before collecting a donation.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
